//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Header, Icon, Input, Label, Message, Popup, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { MasterPackPlan as ApiMasterPackPlan, PodResources as ApiPodResources } from "../../api/data/v1/idata";
import { Model, MemoryFactors as ApiMemoryFactors, CPUFactors as ApiCPUFactors, AddonResources as ApiAddonResources } from "../../api/lib";
import { IConfigurationViewArgs } from "./ConfigurationView";
import { RoleDiskColumns } from "./RoleDiskCell";
import { PackPlanPanel } from "./PackPlanPanel";
import { RelevantNodesPanel } from "./RelevantNodesPanel";
import { useArangoPlatform } from "./useArangoPlatform";

type ResourcesViewArgs = IConfigurationViewArgs & {
  deploymentId?: string;
  dataclusterId?: string;
  podResources?: ApiPodResources;
  // Stamped scheduling-v3 budget. Preferred over the live node-size values.
  usableMemorySize?: string;
  usableCpuSize?: string;
};

type RoleId = "coordinator" | "dbserver" | "gateway" | "loadbalancer";
type AddonRoleId = "gateway" | "loadbalancer";

interface RoleDraft {
  mem: number;
  cpu: number;
}

interface AddonDraft {
  mem: string;
  cpu: string;
}

const ROLE_COLORS: Record<RoleId, string> = {
  coordinator: "#2563eb",
  dbserver: "#0f766e",
  gateway: "#b45309",
  loadbalancer: "#7c3aed",
};

const ROLE_LABELS: Record<RoleId, string> = {
  coordinator: "Coordinator",
  dbserver: "DB server",
  gateway: "Gateway",
  loadbalancer: "Load balancer",
};

const FACTOR_MIN = 0.5;
const FACTOR_MAX = 2.0;
const FACTOR_STEP = 0.05;
/** Agency uses a single shared factor (mem+CPU); API minimum is 1.0. */
const AGENCY_FACTOR_MIN = 1.0;
const AGENCY_FACTOR_MAX = FACTOR_MAX;

/** Parse Kubernetes quantity strings into a comparable base (bytes or cores).
 * Suffixes are case-sensitive per k8s: m vs M, binary Mi/Gi vs decimal M/G.
 * Lowercase binary (mi/gi) is also accepted for convenience. */
const parseQuantity = (q?: string): number | undefined => {
  if (!q) {
    return undefined;
  }
  const trimmed = q.trim();
  const match = trimmed.match(/^([0-9]*\.?[0-9]+)\s*([A-Za-z]*)$/);
  if (!match) {
    return undefined;
  }
  const value = parseFloat(match[1]);
  const unit = match[2] || "";
  if (Number.isNaN(value)) {
    return undefined;
  }
  switch (unit) {
    case "":
      return value;
    // Decimal SI (CPU millicores / decimal memory)
    case "m":
      return value / 1000;
    case "k":
      return value * 1000;
    case "M":
      return value * 1000 * 1000;
    case "G":
      return value * 1000 * 1000 * 1000;
    case "T":
      return value * 1000 * 1000 * 1000 * 1000;
    // Binary SI
    case "Ki":
    case "ki":
      return value * 1024;
    case "Mi":
    case "mi":
      return value * 1024 * 1024;
    case "Gi":
    case "gi":
      return value * 1024 * 1024 * 1024;
    case "Ti":
    case "ti":
      return value * 1024 * 1024 * 1024 * 1024;
    default:
      return undefined;
  }
};

const formatFactor = (n: number) => {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? `${rounded}` : `${rounded}`;
};

const clampFactor = (n: number, min = FACTOR_MIN, max = FACTOR_MAX) => Math.min(max, Math.max(min, Math.round(n / FACTOR_STEP) * FACTOR_STEP));

const factorOrDefault = (v: number | undefined): number => (v === undefined || v === 0 ? 1 : v);

const readMemFactor = (factors: ApiMemoryFactors | undefined, role: RoleId): number => {
  switch (role) {
    case "coordinator":
      return factorOrDefault(factors?.coordinator_factor);
    case "dbserver":
      return factorOrDefault(factors?.dbserver_factor);
    case "gateway":
      return factorOrDefault(factors?.gateway_factor);
    case "loadbalancer":
      return factorOrDefault(factors?.loadbalancer_factor);
  }
};

const readCpuFactor = (factors: ApiCPUFactors | undefined, role: RoleId): number => {
  switch (role) {
    case "coordinator":
      return factorOrDefault(factors?.coordinator_factor);
    case "dbserver":
      return factorOrDefault(factors?.dbserver_factor);
    case "gateway":
      return factorOrDefault(factors?.gateway_factor);
    case "loadbalancer":
      return factorOrDefault(factors?.loadbalancer_factor);
  }
};

const withMemFactor = (factors: ApiMemoryFactors | undefined, role: RoleId, value: number): ApiMemoryFactors => {
  const next: ApiMemoryFactors = {
    coordinator_factor: factorOrDefault(factors?.coordinator_factor),
    dbserver_factor: factorOrDefault(factors?.dbserver_factor),
    gateway_factor: factorOrDefault(factors?.gateway_factor),
    loadbalancer_factor: factorOrDefault(factors?.loadbalancer_factor),
  };
  switch (role) {
    case "coordinator":
      next.coordinator_factor = value;
      break;
    case "dbserver":
      next.dbserver_factor = value;
      break;
    case "gateway":
      next.gateway_factor = value;
      break;
    case "loadbalancer":
      next.loadbalancer_factor = value;
      break;
  }
  return next;
};

const withCpuFactor = (factors: ApiCPUFactors | undefined, role: RoleId, value: number): ApiCPUFactors => {
  const next: ApiCPUFactors = {
    coordinator_factor: factorOrDefault(factors?.coordinator_factor),
    dbserver_factor: factorOrDefault(factors?.dbserver_factor),
    gateway_factor: factorOrDefault(factors?.gateway_factor),
    loadbalancer_factor: factorOrDefault(factors?.loadbalancer_factor),
  };
  switch (role) {
    case "coordinator":
      next.coordinator_factor = value;
      break;
    case "dbserver":
      next.dbserver_factor = value;
      break;
    case "gateway":
      next.gateway_factor = value;
      break;
    case "loadbalancer":
      next.loadbalancer_factor = value;
      break;
  }
  return next;
};

const nearlyEqual = (a: number, b: number) => Math.abs(a - b) < 0.001;

const FactorStepper = ({
  value,
  disabled,
  onChange,
  min = FACTOR_MIN,
  max = FACTOR_MAX,
}: {
  value: number;
  disabled?: boolean;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
    <Input
      type="number"
      size="mini"
      step={FACTOR_STEP}
      min={min}
      max={max}
      value={formatFactor(value)}
      disabled={disabled}
      onChange={(_, data) => {
        const n = parseFloat(`${data.value}`);
        if (!Number.isNaN(n)) {
          onChange(clampFactor(n, min, max));
        }
      }}
      style={{ width: 64 }}
      input={{ style: { fontFamily: "monospace" } }}
    />
    <Button.Group size="mini">
      <Button icon="minus" disabled={disabled || value <= min} onClick={() => onChange(clampFactor(value - FACTOR_STEP, min, max))} />
      <Button icon="plus" disabled={disabled || value >= max} onClick={() => onChange(clampFactor(value + FACTOR_STEP, min, max))} />
    </Button.Group>
  </div>
);

export const ResourcesView = (args: ResourcesViewArgs) => {
  const model = args.model || {};
  const isCluster = Model.isCluster(model.model || "");
  const hasAgency = Model.hasAgency(model.model || "");
  const { isArangoDBPlatformEnabled } = useArangoPlatform(args.organizationId, args.regionId);

  const roles = useMemo(() => {
    const list: RoleId[] = [];
    if (isCluster) {
      list.push("coordinator");
    }
    list.push("dbserver");
    // Match v3 carve: gateway only when platform is enabled.
    if (isArangoDBPlatformEnabled) {
      list.push("gateway");
    }
    // Dedicated (or internal) LB pods — not when sharing the platform LB.
    if (!args.useSharedLoadbalancer) {
      list.push("loadbalancer");
    }
    return list;
  }, [args.useSharedLoadbalancer, isArangoDBPlatformEnabled, isCluster]);

  const savedDrafts = useMemo(() => {
    const map: Record<string, RoleDraft> = {};
    roles.forEach((role) => {
      map[role] = {
        mem: readMemFactor(args.memoryFactors, role),
        cpu: readCpuFactor(args.cpuFactors, role),
      };
    });
    return map;
  }, [args.cpuFactors, args.memoryFactors, roles]);

  const [drafts, setDrafts] = useState<Record<string, RoleDraft>>(savedDrafts);
  const [busyRole, setBusyRole] = useState<RoleId | "all" | "addon" | "agency" | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [packJobMessage, setPackJobMessage] = useState<string | undefined>();
  const [nodesRefresh, setNodesRefresh] = useState(0);
  const [packPlan, setPackPlan] = useState<ApiMasterPackPlan | undefined>();
  const [packPlanLoading, setPackPlanLoading] = useState(false);
  const [packPlanError, setPackPlanError] = useState<string | undefined>();
  const canPack = !!args.requirePermission?.("internal-dashboard.deployment.pack");

  const loadPackPlan = useCallback(
    async (clearJobMessage = false) => {
      if (!args.deploymentId || !canPack) {
        setPackPlan(undefined);
        return;
      }
      setPackPlanLoading(true);
      setPackPlanError(undefined);
      if (clearJobMessage) {
        setPackJobMessage(undefined);
      }
      try {
        const p = await apiClients.idashboardClient.GetPackPlan({ deployment_id: args.deploymentId });
        setPackPlan(p);
      } catch (e) {
        setPackPlan(undefined);
        setPackPlanError((e as { message?: string })?.message || String(e));
      } finally {
        setPackPlanLoading(false);
      }
    },
    [args.deploymentId, canPack]
  );

  useEffect(() => {
    loadPackPlan();
  }, [loadPackPlan]);

  const savedAgencyFactor = factorOrDefault(args.agencyResourceFactor);
  const [agencyDraft, setAgencyDraft] = useState(savedAgencyFactor);

  const [memCoord, setMemCoord] = useState(args.memoryCoordinatorPercent);
  const [memDb, setMemDb] = useState(args.memoryDbServerPercent);
  const [cpuCoord, setCpuCoord] = useState(args.cpuCoordinatorPercent);
  const [cpuDb, setCpuDb] = useState(args.cpuDbServerPercent);
  const [ratioBusy, setRatioBusy] = useState(false);

  const savedAddon = useMemo(() => {
    const ar = args.addonResources || args.nodeSizeDetails.addon_resources;
    const memRes = args.memoryResources;
    const cpuRes = args.cpuResources;
    const read = (role: AddonRoleId): AddonDraft => {
      const v = role === "gateway" ? ar?.gateway : ar?.loadbalancer;
      const memFallback = role === "gateway" ? memRes?.gateway_requests : memRes?.loadbalancer_requests;
      const cpuFallback = role === "gateway" ? cpuRes?.gateway_requests : cpuRes?.loadbalancer_requests;
      return {
        mem: v?.memory || memFallback || "",
        cpu: v?.cpu || cpuFallback || "",
      };
    };
    return {
      gateway: read("gateway"),
      loadbalancer: read("loadbalancer"),
    };
  }, [args.addonResources, args.nodeSizeDetails.addon_resources, args.memoryResources, args.cpuResources]);

  const [addonDrafts, setAddonDrafts] = useState(savedAddon);

  // Server refreshes keep arriving while the tab is open. Adopt them per field:
  // an edited memory scale keeps its draft, while an untouched CPU scale takes the new server value.
  const syncedDrafts = useRef(savedDrafts);
  useEffect(() => {
    setDrafts((prev) => {
      const next: Record<string, RoleDraft> = { ...prev };
      roles.forEach((role) => {
        const saved = savedDrafts[role];
        const synced = syncedDrafts.current[role];
        const current = prev[role];
        if (!saved) {
          return;
        }
        const memEdited = !!current && !!synced && !nearlyEqual(current.mem, synced.mem);
        const cpuEdited = !!current && !!synced && !nearlyEqual(current.cpu, synced.cpu);
        next[role] = {
          mem: memEdited ? current.mem : saved.mem,
          cpu: cpuEdited ? current.cpu : saved.cpu,
        };
      });
      return next;
    });
    syncedDrafts.current = savedDrafts;
  }, [roles, savedDrafts]);

  const syncedAgency = useRef(savedAgencyFactor);
  useEffect(() => {
    setAgencyDraft((prev) => (nearlyEqual(prev, syncedAgency.current) ? savedAgencyFactor : prev));
    syncedAgency.current = savedAgencyFactor;
  }, [savedAgencyFactor]);

  const syncedAddon = useRef(savedAddon);
  useEffect(() => {
    setAddonDrafts((prev) => {
      const next = { ...prev };
      (["gateway", "loadbalancer"] as AddonRoleId[]).forEach((role) => {
        const saved = savedAddon[role];
        const synced = syncedAddon.current[role];
        const current = prev[role];
        if (!saved) {
          return;
        }
        const memEdited = !!current && !!synced && current.mem !== synced.mem;
        const cpuEdited = !!current && !!synced && current.cpu !== synced.cpu;
        next[role] = {
          mem: memEdited ? current.mem : saved.mem,
          cpu: cpuEdited ? current.cpu : saved.cpu,
        };
      });
      return next;
    });
    syncedAddon.current = savedAddon;
  }, [savedAddon]);

  const syncedMemRatio = useRef({ coord: args.memoryCoordinatorPercent, db: args.memoryDbServerPercent });
  useEffect(() => {
    setMemCoord((prev) => (prev === syncedMemRatio.current.coord ? args.memoryCoordinatorPercent : prev));
    setMemDb((prev) => (prev === syncedMemRatio.current.db ? args.memoryDbServerPercent : prev));
    syncedMemRatio.current = { coord: args.memoryCoordinatorPercent, db: args.memoryDbServerPercent };
  }, [args.memoryCoordinatorPercent, args.memoryDbServerPercent]);

  const syncedCpuRatio = useRef({ coord: args.cpuCoordinatorPercent, db: args.cpuDbServerPercent });
  useEffect(() => {
    setCpuCoord((prev) => (prev === syncedCpuRatio.current.coord ? args.cpuCoordinatorPercent : prev));
    setCpuDb((prev) => (prev === syncedCpuRatio.current.db ? args.cpuDbServerPercent : prev));
    syncedCpuRatio.current = { coord: args.cpuCoordinatorPercent, db: args.cpuDbServerPercent };
  }, [args.cpuCoordinatorPercent, args.cpuDbServerPercent]);

  const dirtyRoles = roles.filter((role) => {
    const d = drafts[role];
    const s = savedDrafts[role];
    if (!d || !s) {
      return false;
    }
    return !nearlyEqual(d.mem, s.mem) || !nearlyEqual(d.cpu, s.cpu);
  });

  const agencyDirty = hasAgency && !nearlyEqual(agencyDraft, savedAgencyFactor);
  const canEditAgency = !!args.can_edit_agency_resource_factor;

  const memRatioDirty = isCluster && (memCoord !== args.memoryCoordinatorPercent || memDb !== args.memoryDbServerPercent);
  const cpuRatioDirty = isCluster && (cpuCoord !== args.cpuCoordinatorPercent || cpuDb !== args.cpuDbServerPercent);
  const ratioDirty = memRatioDirty || cpuRatioDirty;
  const canEditRatios = !!(args.can_edit_memory_ratio || args.can_edit_cpu_ratio);

  const applyRatios = () => {
    if (!isCluster || !ratioDirty) {
      return;
    }
    if (memRatioDirty && memCoord + memDb !== 100) {
      setError("Memory ratio must sum to 100");
      return;
    }
    if (cpuRatioDirty && cpuCoord + cpuDb !== 100) {
      setError("CPU ratio must sum to 100");
      return;
    }
    const parts: string[] = [];
    if (memRatioDirty) {
      parts.push(`memory ${memCoord}/${memDb}`);
    }
    if (cpuRatioDirty) {
      parts.push(`CPU ${cpuCoord}/${cpuDb}`);
    }
    args.requireComment({
      defaultComment: `Set ${parts.join(" and ")} ratio`,
      loadingMessage: "Updating ratios…",
      onCommentConfirmation: async () => {
        setRatioBusy(true);
        setError(undefined);
        try {
          if (memRatioDirty && args.can_edit_memory_ratio) {
            const { error: err } = await args.onUpdateMemoryRatio(`${memCoord}/${memDb}`);
            if (err) {
              throw err;
            }
          }
          if (cpuRatioDirty && args.can_edit_cpu_ratio) {
            const { error: err } = await args.onUpdateCPURatio(`${cpuCoord}/${cpuDb}`);
            if (err) {
              throw err;
            }
          }
          args.refreshDeploymentInfo && args.refreshDeploymentInfo();
        } catch (e) {
          setError(`${e}`);
        } finally {
          setRatioBusy(false);
        }
      },
    });
  };

  const revertRatios = () => {
    setMemCoord(args.memoryCoordinatorPercent);
    setMemDb(args.memoryDbServerPercent);
    setCpuCoord(args.cpuCoordinatorPercent);
    setCpuDb(args.cpuDbServerPercent);
  };

  const serverRequestFor = (role: RoleId, kind: "mem" | "cpu"): string | undefined => {
    const res = kind === "mem" ? args.memoryResources : args.cpuResources;
    if (!res) {
      return undefined;
    }
    switch (role) {
      case "coordinator":
        return res.coordinator_requests;
      case "dbserver":
        return res.dbserver_requests;
      case "gateway":
        return res.gateway_requests;
      case "loadbalancer":
        return res.loadbalancer_requests;
    }
  };

  const podRequestFor = (role: RoleId, kind: "mem" | "cpu"): string | undefined => {
    const pod = args.podResources;
    if (!pod) {
      return undefined;
    }
    switch (role) {
      case "coordinator":
        return kind === "mem" ? pod.coordinator_memory : pod.coordinator_cpu;
      case "dbserver":
        return kind === "mem" ? pod.dbserver_memory : pod.dbserver_cpu;
      case "gateway":
        return kind === "mem" ? pod.gateway_memory : pod.gateway_cpu;
      case "loadbalancer":
        return kind === "mem" ? pod.loadbalancer_memory : pod.loadbalancer_cpu;
    }
  };

  const requestFor = (role: RoleId, kind: "mem" | "cpu"): string | undefined => {
    return podRequestFor(role, kind) || serverRequestFor(role, kind);
  };

  const serversFor = (role: RoleId): number | undefined => {
    switch (role) {
      case "coordinator":
        return args.servers?.coordinators;
      case "dbserver":
        return args.servers?.dbservers;
      case "gateway":
        return args.size?.gateways;
      case "loadbalancer":
        // Dedicated envoy LB replicas match coordinator count on clusters (1 on single).
        return isCluster ? args.servers?.coordinators : 1;
    }
  };

  const agentServers = args.size?.agents;

  const addonDirtyRoles = (["gateway", "loadbalancer"] as AddonRoleId[]).filter((role) => {
    if (!roles.includes(role)) {
      return false;
    }
    const d = addonDrafts[role];
    const s = savedAddon[role];
    return d.mem !== s.mem || d.cpu !== s.cpu;
  });
  const addonDirty = addonDirtyRoles.length > 0;
  const canEditAddon = !!args.canUpdateAddonResources && !!args.onUpdateAddonResources;

  const applyAddon = () => {
    if (!args.onUpdateAddonResources || !addonDirty) {
      return;
    }
    const next: ApiAddonResources = {
      gateway: roles.includes("gateway")
        ? { memory: addonDrafts.gateway.mem, cpu: addonDrafts.gateway.cpu }
        : args.addonResources?.gateway || args.nodeSizeDetails.addon_resources?.gateway,
      loadbalancer: roles.includes("loadbalancer")
        ? { memory: addonDrafts.loadbalancer.mem, cpu: addonDrafts.loadbalancer.cpu }
        : args.addonResources?.loadbalancer || args.nodeSizeDetails.addon_resources?.loadbalancer,
    };
    args.requireComment({
      defaultComment: `Set addon carve resources gateway=${addonDrafts.gateway.mem}/${addonDrafts.gateway.cpu} lb=${addonDrafts.loadbalancer.mem}/${addonDrafts.loadbalancer.cpu}`,
      loadingMessage: "Updating addon carve resources…",
      onCommentConfirmation: async () => {
        setBusyRole("addon");
        setError(undefined);
        try {
          const { error: err } = await args.onUpdateAddonResources!(next);
          if (err) {
            throw err;
          }
          args.refreshDeploymentInfo && args.refreshDeploymentInfo();
        } catch (e) {
          setError(`${e}`);
        } finally {
          setBusyRole(undefined);
        }
      },
    });
  };

  const usableMem = args.usableMemorySize || args.nodeSizeDetails.usable_memory_size;
  const usableCpu = args.usableCpuSize || args.nodeSizeDetails.usable_cpu_size;
  const usableMemN = parseQuantity(usableMem);
  const usableCpuN = parseQuantity(usableCpu);

  const memSegments = roles
    .map((role) => ({
      role,
      value: parseQuantity(requestFor(role, "mem")) || 0,
      label: requestFor(role, "mem") || "—",
    }))
    .filter((s) => s.value > 0);
  const cpuSegments = roles
    .map((role) => ({
      role,
      value: parseQuantity(requestFor(role, "cpu")) || 0,
      label: requestFor(role, "cpu") || "—",
    }))
    .filter((s) => s.value > 0);

  const memUsed = memSegments.reduce((a, s) => a + s.value, 0);
  const cpuUsed = cpuSegments.reduce((a, s) => a + s.value, 0);
  const memOver = usableMemN !== undefined && memUsed > usableMemN * 1.001;
  const cpuOver = usableCpuN !== undefined && cpuUsed > usableCpuN * 1.001;

  const canEditFactors = !!(args.canUpdateMemoryFactors || args.canUpdateCPUFactors);
  const showOverrideCols = isCluster || roles.includes("gateway") || roles.includes("loadbalancer");

  const applyRole = (role: RoleId, next: RoleDraft) => {
    if (!args.onUpdateMemoryFactorsValues || !args.onUpdateCPUFactorsValues) {
      return;
    }
    const memChanged = !nearlyEqual(next.mem, savedDrafts[role].mem);
    const cpuChanged = !nearlyEqual(next.cpu, savedDrafts[role].cpu);
    args.requireComment({
      defaultComment: `Set ${ROLE_LABELS[role]} factors to mem=${formatFactor(next.mem)} cpu=${formatFactor(next.cpu)}`,
      loadingMessage: `Updating ${ROLE_LABELS[role]} factors…`,
      onCommentConfirmation: async () => {
        setBusyRole(role);
        setError(undefined);
        try {
          if (memChanged && args.canUpdateMemoryFactors) {
            const { error: err } = await args.onUpdateMemoryFactorsValues!(withMemFactor(args.memoryFactors, role, next.mem));
            if (err) {
              throw err;
            }
          }
          if (cpuChanged && args.canUpdateCPUFactors) {
            const { error: err } = await args.onUpdateCPUFactorsValues!(withCpuFactor(args.cpuFactors, role, next.cpu));
            if (err) {
              throw err;
            }
          }
          args.refreshDeploymentInfo && args.refreshDeploymentInfo();
        } catch (e) {
          setError(`${e}`);
        } finally {
          setBusyRole(undefined);
        }
      },
    });
  };

  const applyAllDirty = () => {
    if (!args.onUpdateMemoryFactorsValues || !args.onUpdateCPUFactorsValues || dirtyRoles.length === 0) {
      return;
    }
    let memNext = args.memoryFactors || {};
    let cpuNext = args.cpuFactors || {};
    dirtyRoles.forEach((role) => {
      memNext = withMemFactor(memNext, role, drafts[role].mem);
      cpuNext = withCpuFactor(cpuNext, role, drafts[role].cpu);
    });
    const summary = dirtyRoles.map((r) => `${ROLE_LABELS[r]} ${formatFactor(drafts[r].mem)}/${formatFactor(drafts[r].cpu)}`).join(", ");
    args.requireComment({
      defaultComment: `Apply resource factors: ${summary}`,
      loadingMessage: "Updating resource factors…",
      onCommentConfirmation: async () => {
        setBusyRole("all");
        setError(undefined);
        try {
          if (args.canUpdateMemoryFactors) {
            const { error: err } = await args.onUpdateMemoryFactorsValues!(memNext);
            if (err) {
              throw err;
            }
          }
          if (args.canUpdateCPUFactors) {
            const { error: err } = await args.onUpdateCPUFactorsValues!(cpuNext);
            if (err) {
              throw err;
            }
          }
          args.refreshDeploymentInfo && args.refreshDeploymentInfo();
        } catch (e) {
          setError(`${e}`);
        } finally {
          setBusyRole(undefined);
        }
      },
    });
  };

  const applyAgency = (next: number) => {
    if (!canEditAgency) {
      return;
    }
    args.requireComment({
      defaultComment: `Set agency resources factor to ${formatFactor(next)}`,
      loadingMessage: "Updating agency resources factor…",
      onCommentConfirmation: async () => {
        setBusyRole("agency");
        setError(undefined);
        try {
          const { error: err } = await args.onUpdateAgencyResourceFactor(`${next}`);
          if (err) {
            throw err;
          }
          args.refreshDeploymentInfo && args.refreshDeploymentInfo();
        } catch (e) {
          setError(`${e}`);
        } finally {
          setBusyRole(undefined);
        }
      },
    });
  };

  const renderBar = (segments: { role: RoleId; value: number; label: string }[], usable?: number) => {
    const total = usable && usable > 0 ? usable : segments.reduce((a, s) => a + s.value, 0) || 1;
    return (
      <div
        style={{
          display: "flex",
          height: 28,
          borderRadius: 5,
          overflow: "hidden",
          background: "#eef2f7",
          border: "1px solid #e5e7eb",
          marginTop: 8,
        }}
      >
        {segments.map((s) => (
          <Popup
            key={s.role}
            content={`${ROLE_LABELS[s.role]}: ${s.label}`}
            trigger={
              <div
                style={{
                  width: `${Math.max(2, (s.value / total) * 100)}%`,
                  background: ROLE_COLORS[s.role],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 10,
                  fontFamily: "monospace",
                  minWidth: 0,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  padding: "0 4px",
                }}
              >
                {s.value / total > 0.12 ? ROLE_LABELS[s.role] : ""}
              </div>
            }
          />
        ))}
      </div>
    );
  };

  return (
    <div>
      <Header sub>Resources</Header>
      <p style={{ color: "#6b7280", fontSize: 13, maxWidth: 72 * 8, marginTop: 0 }}>
        Scheduling v3 carves the usable budget into roles. Memory and CPU requests are pod totals, including sidecar requests. Mem/CPU override: coordinator/DB
        server ratios, or absolute gateway/LB carve requests. Mem/CPU scales multiply the post-carve server share independently (range {FACTOR_MIN}–{FACTOR_MAX}
        ). Edit a row, then Apply — or hit 1× to reset identity.
      </p>

      {error && <Message negative onDismiss={() => setError(undefined)} content={error} />}

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          padding: "14px 16px",
          marginBottom: 14,
        }}
      >
        <Header as="h4" style={{ marginTop: 0 }}>
          Budget{" "}
          <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 12 }}>
            DB server + coordinator + addons
            {args.nodeSizeDetails.name || model.node_size_id ? ` · ${args.nodeSizeDetails.name || model.node_size_id}` : ""}
          </span>
        </Header>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "18px 28px", marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 11, textTransform: "uppercase", color: "#6b7280", letterSpacing: "0.04em" }}>Nominal</div>
            <div style={{ fontFamily: "monospace", fontWeight: 600 }}>{args.nodeSizeDetails.memory_size ? `${args.nodeSizeDetails.memory_size} GiB` : "—"}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, textTransform: "uppercase", color: "#6b7280", letterSpacing: "0.04em" }}>Usable memory</div>
            <div style={{ fontFamily: "monospace", fontWeight: 600 }}>{usableMem || "—"}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, textTransform: "uppercase", color: "#6b7280", letterSpacing: "0.04em" }}>Usable CPU</div>
            <div style={{ fontFamily: "monospace", fontWeight: 600 }}>{usableCpu || "—"}</div>
          </div>
          <div>
            <Label size="small" color={memOver || cpuOver ? "orange" : "green"} basic>
              {memOver || cpuOver ? "Over usable (after scale)" : "Within usable"}
            </Label>
          </div>
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "monospace" }}>Memory requests</div>
        {renderBar(memSegments, usableMemN)}
        <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "monospace", marginTop: 10 }}>CPU requests</div>
        {renderBar(cpuSegments, usableCpuN)}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 16px", marginTop: 8, fontSize: 12, color: "#6b7280" }}>
          {roles.map((role) => (
            <span key={role}>
              <i
                style={{
                  display: "inline-block",
                  width: 9,
                  height: 9,
                  borderRadius: 2,
                  background: ROLE_COLORS[role],
                  marginRight: 5,
                  verticalAlign: "middle",
                }}
              />
              {ROLE_LABELS[role]}
            </span>
          ))}
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          padding: "14px 16px",
          marginBottom: 14,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <Header as="h4" style={{ marginTop: 0, marginBottom: 0 }}>
            Per-role allocation{" "}
            <span style={{ fontWeight: 400, color: "#6b7280", fontSize: 12 }}>{isCluster ? "carve overrides + independent scales" : "independent scales"}</span>
          </Header>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {ratioDirty && canEditRatios && (
              <Button.Group size="mini">
                <Button color="orange" content="Apply ratios" loading={ratioBusy} disabled={ratioBusy || !!busyRole} onClick={applyRatios} />
                <Button content="Revert ratios" disabled={ratioBusy || !!busyRole} onClick={revertRatios} />
              </Button.Group>
            )}
            {addonDirty && canEditAddon && (
              <Button.Group size="mini">
                <Button color="orange" content="Apply carve" loading={busyRole === "addon"} disabled={!!busyRole || ratioBusy} onClick={applyAddon} />
                <Button content="Revert carve" disabled={!!busyRole || ratioBusy} onClick={() => setAddonDrafts(savedAddon)} />
              </Button.Group>
            )}
            {dirtyRoles.length > 0 && canEditFactors && (
              <Button.Group size="mini">
                <Button
                  color="orange"
                  content={`Apply ${dirtyRoles.length} scale${dirtyRoles.length === 1 ? "" : "s"}`}
                  loading={busyRole === "all"}
                  disabled={!!busyRole || ratioBusy}
                  onClick={applyAllDirty}
                />
                <Button content="Discard scales" disabled={!!busyRole || ratioBusy} onClick={() => setDrafts(savedDrafts)} />
              </Button.Group>
            )}
            {agencyDirty && canEditAgency && (
              <Button.Group size="mini">
                <Button
                  color="orange"
                  content="Apply agency"
                  loading={busyRole === "agency"}
                  disabled={!!busyRole || ratioBusy}
                  onClick={() => applyAgency(agencyDraft)}
                />
                <Button content="Revert agency" disabled={!!busyRole || ratioBusy} onClick={() => setAgencyDraft(savedAgencyFactor)} />
              </Button.Group>
            )}
          </div>
        </div>

        <Table basic="very" compact style={{ marginTop: 12 }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Role</Table.HeaderCell>
              <Table.HeaderCell>Servers</Table.HeaderCell>
              {showOverrideCols && <Table.HeaderCell>Mem override</Table.HeaderCell>}
              <Table.HeaderCell>Memory request</Table.HeaderCell>
              <Table.HeaderCell>Mem scale</Table.HeaderCell>
              {showOverrideCols && <Table.HeaderCell>CPU override</Table.HeaderCell>}
              <Table.HeaderCell>CPU request</Table.HeaderCell>
              <Table.HeaderCell>CPU scale</Table.HeaderCell>
              <Table.HeaderCell>Disk size</Table.HeaderCell>
              <Table.HeaderCell>Performance</Table.HeaderCell>
              <Table.HeaderCell>Throughput</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {roles.map((role) => {
              const draft = drafts[role] || savedDrafts[role];
              const saved = savedDrafts[role];
              const dirty = !nearlyEqual(draft.mem, saved.mem) || !nearlyEqual(draft.cpu, saved.cpu);
              const ratioRow = isCluster && (role === "coordinator" || role === "dbserver");
              const addonRow = role === "gateway" || role === "loadbalancer";
              const addonDraft = addonRow ? addonDrafts[role] : undefined;
              const addonSaved = addonRow ? savedAddon[role] : undefined;
              const addonRowDirty = !!(addonDraft && addonSaved && (addonDraft.mem !== addonSaved.mem || addonDraft.cpu !== addonSaved.cpu));
              const rowHighlight = dirty || (ratioDirty && ratioRow) || addonRowDirty;
              const atIdentity = nearlyEqual(saved.mem, 1) && nearlyEqual(saved.cpu, 1);
              const memReq = requestFor(role, "mem");
              const cpuReq = requestFor(role, "cpu");
              const serverMem = serverRequestFor(role, "mem");
              const impliedBaseMem = serverMem && draft.mem ? `${formatFactor((parseQuantity(serverMem) || 0) / draft.mem)} (base≈req/scale)` : undefined;
              const memPct = role === "coordinator" ? memCoord : role === "dbserver" ? memDb : undefined;
              const cpuPct = role === "coordinator" ? cpuCoord : role === "dbserver" ? cpuDb : undefined;

              return (
                <Table.Row key={role} style={rowHighlight ? { background: "#fffaf5" } : undefined}>
                  <Table.Cell>
                    <span style={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <i
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: ROLE_COLORS[role],
                          display: "inline-block",
                        }}
                      />
                      {isCluster || role !== "dbserver" ? ROLE_LABELS[role] : "Single server"}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>{serversFor(role) ?? "—"}</span>
                  </Table.Cell>
                  {showOverrideCols && (
                    <Table.Cell>
                      {ratioRow ? (
                        <Input
                          type="number"
                          size="mini"
                          min={1}
                          max={99}
                          value={memPct}
                          disabled={!args.can_edit_memory_ratio || ratioBusy || !!busyRole}
                          onChange={(_, d) => {
                            const n = parseInt(`${d.value}`, 10);
                            if (Number.isNaN(n)) {
                              return;
                            }
                            if (role === "coordinator") {
                              setMemCoord(n);
                              setMemDb(100 - n);
                            } else {
                              setMemDb(n);
                              setMemCoord(100 - n);
                            }
                          }}
                          style={{ width: 64 }}
                          input={{ style: { fontFamily: "monospace" } }}
                          label={{ basic: true, content: "%" }}
                          labelPosition="right"
                        />
                      ) : addonRow && addonDraft ? (
                        <Input
                          size="mini"
                          value={addonDraft.mem}
                          disabled={!canEditAddon || !!busyRole || ratioBusy}
                          placeholder="e.g. 384Mi"
                          onChange={(_, d) =>
                            setAddonDrafts((prev) => ({
                              ...prev,
                              [role]: { ...prev[role as AddonRoleId], mem: `${d.value}` },
                            }))
                          }
                          style={{ width: 96 }}
                          input={{ style: { fontFamily: "monospace" } }}
                        />
                      ) : (
                        <span style={{ color: "#9ca3af" }}>—</span>
                      )}
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>{memReq || "—"}</span>
                    {impliedBaseMem && dirty && <div style={{ fontSize: 11, color: "#6b7280" }}>{impliedBaseMem}</div>}
                  </Table.Cell>
                  <Table.Cell>
                    <FactorStepper
                      value={draft.mem}
                      disabled={!args.canUpdateMemoryFactors || !!busyRole || ratioBusy}
                      onChange={(v) => setDrafts((prev) => ({ ...prev, [role]: { ...prev[role], mem: v } }))}
                    />
                  </Table.Cell>
                  {showOverrideCols && (
                    <Table.Cell>
                      {ratioRow ? (
                        <Input
                          type="number"
                          size="mini"
                          min={1}
                          max={99}
                          value={cpuPct}
                          disabled={!args.can_edit_cpu_ratio || ratioBusy || !!busyRole}
                          onChange={(_, d) => {
                            const n = parseInt(`${d.value}`, 10);
                            if (Number.isNaN(n)) {
                              return;
                            }
                            if (role === "coordinator") {
                              setCpuCoord(n);
                              setCpuDb(100 - n);
                            } else {
                              setCpuDb(n);
                              setCpuCoord(100 - n);
                            }
                          }}
                          style={{ width: 64 }}
                          input={{ style: { fontFamily: "monospace" } }}
                          label={{ basic: true, content: "%" }}
                          labelPosition="right"
                        />
                      ) : addonRow && addonDraft ? (
                        <Input
                          size="mini"
                          value={addonDraft.cpu}
                          disabled={!canEditAddon || !!busyRole || ratioBusy}
                          placeholder="e.g. 375m"
                          onChange={(_, d) =>
                            setAddonDrafts((prev) => ({
                              ...prev,
                              [role]: { ...prev[role as AddonRoleId], cpu: `${d.value}` },
                            }))
                          }
                          style={{ width: 96 }}
                          input={{ style: { fontFamily: "monospace" } }}
                        />
                      ) : (
                        <span style={{ color: "#9ca3af" }}>—</span>
                      )}
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>{cpuReq || "—"}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <FactorStepper
                      value={draft.cpu}
                      disabled={!args.canUpdateCPUFactors || !!busyRole || ratioBusy}
                      onChange={(v) => setDrafts((prev) => ({ ...prev, [role]: { ...prev[role], cpu: v } }))}
                    />
                  </Table.Cell>
                  <RoleDiskColumns
                    {...args}
                    kind={role === "dbserver" ? "dbserver" : "none"}
                    diskSize={args.servers?.dbserver_disk_size}
                    diskPerformanceId={args.diskPerformanceId}
                    regionId={args.regionId}
                    nodeSizeId={model.node_size_id}
                    nodeDiskSize={model.node_disk_size}
                    volumeInfo={args.dbserverVolumeInfo}
                    lastDiskPerformanceUpdatedAt={args.lastDiskPerformanceUpdatedAt}
                    lastDiskSizeUpdatedAt={args.lastDiskSizeUpdatedAt}
                    diskRateLimitPeriod={args.diskRateLimitPeriod}
                    diskRateLimitActive={args.diskRateLimitActive}
                    canEditDiskSize={args.canEditDiskSize}
                    canEditDiskSizeIgnoreMax={args.canEditDiskSizeIgnoreMax}
                    onUpdateDiskSize={args.onUpdateDiskSize}
                    canEditDiskPerformance={args.canEditDiskPerformance}
                    onEditDiskPerformance={args.onEditDiskPerformance}
                    onCancelEditDiskPerformance={args.onCancelEditDiskPerformance}
                    editDiskPerformance={args.editDiskPerformance}
                    onDiskPerformanceChange={args.onDiskPerformanceChange}
                    canEditVolumeInfos={args.canEditVolumeInfos}
                    onEditVolumeInfos={args.onEditDBServersVolumeInfos}
                    onCancelEditVolumeInfos={args.onCancelEditDBServersVolumeInfos}
                    editVolumeInfos={args.editDBServersVolumeInfos}
                    onSaveVolumeInfos={(type, iops, throughput) => args.onSaveVolumeInfos(undefined, { type, iops, throughput })}
                    processingUpdateVolumeInfos={args.processingUpdateVolumeInfos}
                    createComment={args.createComment}
                  />
                  <Table.Cell textAlign="right">
                    {dirty ? (
                      <Button.Group size="mini">
                        <Popup
                          content="Save mem + CPU scale for this role"
                          trigger={
                            <Button
                              primary
                              icon
                              loading={busyRole === role}
                              disabled={!!busyRole || ratioBusy || !canEditFactors}
                              onClick={() => applyRole(role, draft)}
                            >
                              <Icon name="check" />
                            </Button>
                          }
                        />
                        <Popup
                          content="Discard draft"
                          trigger={
                            <Button icon disabled={!!busyRole || ratioBusy} onClick={() => setDrafts((prev) => ({ ...prev, [role]: { ...saved } }))}>
                              <Icon name="undo" />
                            </Button>
                          }
                        />
                      </Button.Group>
                    ) : (
                      <Popup
                        content={atIdentity ? "Already at 1×" : "Reset this role to 1× (identity)"}
                        trigger={
                          <Button
                            size="mini"
                            basic
                            disabled={!!busyRole || ratioBusy || atIdentity || !canEditFactors}
                            onClick={() => {
                              const next = { mem: 1, cpu: 1 };
                              setDrafts((prev) => ({ ...prev, [role]: next }));
                              applyRole(role, next);
                            }}
                          >
                            1×
                          </Button>
                        }
                      />
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
            {hasAgency && (
              <Table.Row style={agencyDirty ? { background: "#fffaf5" } : undefined}>
                <Table.Cell>
                  <span style={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <i
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#64748b",
                        display: "inline-block",
                      }}
                    />
                    Agents
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span style={{ fontFamily: "monospace", fontSize: 12 }}>{agentServers ?? "—"}</span>
                </Table.Cell>
                {showOverrideCols && <Table.Cell style={{ color: "#9ca3af" }}>—</Table.Cell>}
                <Table.Cell>
                  <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {args.podResources?.agent_memory || args.memoryResources?.agent_requests || "—"}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <FactorStepper
                    value={agencyDraft}
                    min={AGENCY_FACTOR_MIN}
                    max={AGENCY_FACTOR_MAX}
                    disabled={!canEditAgency || !!busyRole || ratioBusy}
                    onChange={setAgencyDraft}
                  />
                </Table.Cell>
                {showOverrideCols && <Table.Cell style={{ color: "#9ca3af" }}>—</Table.Cell>}
                <Table.Cell>
                  <span style={{ fontFamily: "monospace", fontSize: 12 }}>{args.podResources?.agent_cpu || args.cpuResources?.agent_requests || "—"}</span>
                </Table.Cell>
                <Table.Cell>
                  <span style={{ color: "#6b7280", fontSize: 12 }}>shared</span>
                </Table.Cell>
                <RoleDiskColumns
                  {...args}
                  kind="agent"
                  diskSize={args.size?.agent_disk_size}
                  regionId={args.regionId}
                  nodeSizeId={model.node_size_id}
                  nodeDiskSize={model.node_disk_size}
                  volumeInfo={args.agentsVolumeInfo}
                  lastDiskPerformanceUpdatedAt={args.lastDiskPerformanceUpdatedAt}
                  lastDiskSizeUpdatedAt={args.lastDiskSizeUpdatedAt}
                  diskRateLimitPeriod={args.diskRateLimitPeriod}
                  diskRateLimitActive={args.diskRateLimitActive}
                  canEditDiskSize={false}
                  canEditDiskSizeIgnoreMax={false}
                  onUpdateDiskSize={async () => ({})}
                  canEditDiskPerformance={false}
                  onEditDiskPerformance={() => {}}
                  onCancelEditDiskPerformance={() => {}}
                  editDiskPerformance={false}
                  onDiskPerformanceChange={() => {}}
                  canEditVolumeInfos={args.canEditVolumeInfos}
                  onEditVolumeInfos={args.onEditAgentsVolumeInfos}
                  onCancelEditVolumeInfos={args.onCancelEditAgentsVolumeInfos}
                  editVolumeInfos={args.editAgentsVolumeInfos}
                  onSaveVolumeInfos={(type, iops, throughput) => args.onSaveVolumeInfos({ type, iops, throughput }, undefined)}
                  processingUpdateVolumeInfos={args.processingUpdateVolumeInfos}
                  createComment={args.createComment}
                />
                <Table.Cell textAlign="right">
                  {agencyDirty ? (
                    <Button.Group size="mini">
                      <Popup
                        content="Save agency scale (applies to memory and CPU)"
                        trigger={
                          <Button
                            primary
                            icon
                            loading={busyRole === "agency"}
                            disabled={!!busyRole || ratioBusy || !canEditAgency}
                            onClick={() => applyAgency(agencyDraft)}
                          >
                            <Icon name="check" />
                          </Button>
                        }
                      />
                      <Popup
                        content="Discard draft"
                        trigger={
                          <Button icon disabled={!!busyRole || ratioBusy} onClick={() => setAgencyDraft(savedAgencyFactor)}>
                            <Icon name="undo" />
                          </Button>
                        }
                      />
                    </Button.Group>
                  ) : (
                    <Popup
                      content={nearlyEqual(savedAgencyFactor, 1) ? "Already at 1×" : "Reset agency factor to 1×"}
                      trigger={
                        <Button
                          size="mini"
                          basic
                          disabled={!!busyRole || ratioBusy || nearlyEqual(savedAgencyFactor, 1) || !canEditAgency}
                          onClick={() => {
                            setAgencyDraft(1);
                            applyAgency(1);
                          }}
                        >
                          1×
                        </Button>
                      }
                    />
                  )}
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
        <div style={{ fontSize: 12, color: "#6b7280", borderTop: "1px dashed #e5e7eb", paddingTop: 10 }}>
          {isCluster && (
            <>
              Mem/CPU override on coordinator and DB server overrides how the auto pool is carved (always sum to 100). Gateway/LB are fixed carve — no share.{" "}
            </>
          )}
          Scales use ± / Apply per row, or queue several and use <em>Apply N scales</em>. 1× writes identity factors after a comment.
          {hasAgency && <> Agents use one shared factor for mem and CPU (min {AGENCY_FACTOR_MIN}×) — not independent v3 scales.</>}
        </div>
      </div>

      {packJobMessage && <Message info content={packJobMessage} style={{ marginTop: 16 }} />}
      <PackPlanPanel
        deploymentId={args.deploymentId}
        plan={packPlan}
        loading={packPlanLoading}
        error={packPlanError}
        requireComment={args.requireComment}
        requirePermission={args.requirePermission}
        onPackJobMessage={setPackJobMessage}
        onPlan={setPackPlan}
        onRefresh={() => {
          loadPackPlan(true);
          setNodesRefresh((n) => n + 1);
        }}
      />

      <RelevantNodesPanel deploymentId={args.deploymentId} dataclusterId={args.dataclusterId} plan={packPlan} refreshToken={nodesRefresh} />
    </div>
  );
};
